import React from 'react';
import {
  IonPage,
  IonContent,
  IonItem,
  IonLabel,
  IonList,
  IonText,
  IonIcon,
  IonBadge,
  useIonViewWillLeave,
} from '@ionic/react';
import {
  checkmarkCircleOutline,
  documentTextOutline,
  shieldCheckmarkOutline,
} from 'ionicons/icons';
import { FooterDock, FooterDockRow, SmartCaseButton } from '@/modules/footer';
import { PhoneLogic } from '@/modules/phone';
import { useUserContext } from '@/modules/user/useUserContext';
import { ClaimsLogic } from '@/modules/idv';
import { useProfileWizardContext } from '@/contexts/ProfileWizardContext';
import { ProfileWizardLogic } from './logic';
import ProfileWizardHeader from './ProfileWizardHeader';
import { pxToRem } from '@/modules/config/constants';

const WelcomeStep: React.FC = () => {
  const {
    familiarName,
    allowedName,
    phone: contextPhone,
    email: contextEmail,
    claims,
    hasUserId,
  } = useUserContext();
  const { navigateWithOrigin, progress } = useProfileWizardContext();

  useIonViewWillLeave(() => {
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
  });

  const handleContinue = () => {
    navigateWithOrigin('/profile/steps/attribution', 'forward');
  };

  const phoneVerified = ClaimsLogic.isPhoneVerified(claims);
  const emailVerified = ClaimsLogic.isEmailVerified(claims);

  const displayFamiliarName = familiarName || 'Not set';
  const displayAllowedName = allowedName || 'Not set';
  const displayPhone = contextPhone
    ? PhoneLogic.format(contextPhone.value)
    : 'Not set';
  const displayEmail = contextEmail || 'Not set';

  // Field labels are gray; field values are the default (dark/black) text color.
  const fieldLabelStyle: React.CSSProperties = {
    color: 'var(--ion-color-medium)',
  };
  const fieldValueStyle: React.CSSProperties = {
    color: 'var(--ion-text-color)',
  };

  // Two variants share one page + footer: a first-time visitor (no user id yet)
  // gets an intro plus the ToS/Privacy links; a returning user gets a summary of
  // what's on file. Either way the single primary action starts editing.
  return (
    <IonPage>
      <ProfileWizardHeader
        title={ProfileWizardLogic.titleForStep('welcome')}
        progress={progress}
      />
      <IonContent>
        <div style={{ padding: '20px 20px 120px' }}>
          {hasUserId ? (
            <>
              <IonText>
                <p style={{ fontSize: pxToRem(16), marginBottom: '20px' }}>
                  Here's what we have for you.
                </p>
              </IonText>
              <IonItem>
                <IonLabel>
                  <h3 style={fieldLabelStyle}>Familiar Name</h3>
                  <p style={fieldValueStyle}>{displayFamiliarName}</p>
                  <IonText color="medium">
                    <p style={{ fontSize: pxToRem(12) }}>
                      (stored only on your device)
                    </p>
                  </IonText>
                </IonLabel>
              </IonItem>
              <IonItem>
                <IonLabel>
                  <h3 style={fieldLabelStyle}>Allowed Name</h3>
                  <p style={fieldValueStyle}>{displayAllowedName}</p>
                  <IonText color="medium">
                    <p style={{ fontSize: pxToRem(12) }}>
                      (stored only on your device)
                    </p>
                  </IonText>
                </IonLabel>
              </IonItem>
              <IonItem>
                <IonLabel>
                  <h3 style={fieldLabelStyle}>Phone</h3>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                    }}
                  >
                    <p style={{ margin: 0, ...fieldValueStyle }}>
                      {displayPhone}
                    </p>
                    {phoneVerified && (
                      <IonBadge color="success">
                        <IonIcon
                          icon={checkmarkCircleOutline}
                          style={{ marginRight: '4px' }}
                        />
                        Verified
                      </IonBadge>
                    )}
                    {displayPhone !== 'Not set' && !phoneVerified && (
                      <IonBadge color="warning">Expired</IonBadge>
                    )}
                  </div>
                  <IonText color="medium">
                    <p style={{ fontSize: pxToRem(12) }}>
                      (stored on your device, encrypted on our server)
                    </p>
                  </IonText>
                </IonLabel>
              </IonItem>
              <IonItem>
                <IonLabel>
                  <h3 style={fieldLabelStyle}>Email</h3>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                    }}
                  >
                    <p style={{ margin: 0, ...fieldValueStyle }}>
                      {displayEmail}
                    </p>
                    {emailVerified && (
                      <IonBadge color="success">
                        <IonIcon
                          icon={checkmarkCircleOutline}
                          style={{ marginRight: '4px' }}
                        />
                        Verified
                      </IonBadge>
                    )}
                    {displayEmail !== 'Not set' && !emailVerified && (
                      <IonBadge color="warning">Expired</IonBadge>
                    )}
                  </div>
                  <IonText color="medium">
                    <p style={{ fontSize: pxToRem(12) }}>
                      (stored on our server)
                    </p>
                  </IonText>
                </IonLabel>
              </IonItem>
            </>
          ) : (
            <>
              <IonText>
                <p style={{ fontSize: pxToRem(16), marginBottom: '20px' }}>
                  The less we know about you, the better. But we do need a few
                  things to get you started.
                </p>
              </IonText>
              <div
                style={{
                  padding: '20px 0',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '10px',
                }}
              >
                <IonText color="medium">
                  <p style={{ fontSize: pxToRem(12), margin: '0 0 4px' }}>
                    Some bits you might want to know about us:
                  </p>
                </IonText>
                <IonList inset={false}>
                  <IonItem
                    href="/tos"
                    detail
                    lines="full"
                    data-testid="welcome-terms-item"
                  >
                    <IonIcon icon={documentTextOutline} slot="start" />
                    <IonLabel>Terms of Service</IonLabel>
                  </IonItem>
                  <IonItem
                    href="/privacy"
                    detail
                    lines="none"
                    data-testid="welcome-privacy-item"
                  >
                    <IonIcon icon={shieldCheckmarkOutline} slot="start" />
                    <IonLabel>Privacy Policy</IonLabel>
                  </IonItem>
                </IonList>
              </div>
            </>
          )}
        </div>
      </IonContent>
      <FooterDock background="transparent">
        <FooterDockRow landmark>
          <SmartCaseButton
            expand="block"
            onClick={handleContinue}
            color="primary"
            data-testid="profile-wizard-next"
            style={{ margin: 0 }}
          >
            {hasUserId ? 'Make changes' : "Let's go!"}
          </SmartCaseButton>
        </FooterDockRow>
      </FooterDock>
    </IonPage>
  );
};

export default WelcomeStep;
